/**
 * 3D Room Visualizer — Material Factory
 *
 * Creates color-accurate Three.js materials for product visualization.
 * Addresses the white-looks-gray problem with proper material configuration.
 */

import * as THREE from 'three';
import { applyWood } from './woodTexture';

// Product color hex values
const COLOR_WHITE = 0xffffff;
const COLOR_GRAY = 0xd0d3d4; // Pantone 427 CP

const COLOR_WHITE_HEX = '#FFFFFF';
const COLOR_GRAY_HEX = '#D0D3D4';

/**
 * Get hex color value for a product color name.
 */
export function getProductColorHex(color: 'White' | 'Gray'): string {
  return color === 'White' ? COLOR_WHITE_HEX : COLOR_GRAY_HEX;
}

/**
 * Get Three.js color integer for a product color name.
 */
export function getProductColor(color: 'White' | 'Gray'): number {
  return color === 'White' ? COLOR_WHITE : COLOR_GRAY;
}

/**
 * Create a material for board/panel surfaces.
 * Uses a blend of diffuse + emissive: emissive keeps whites bright and
 * lighting-stable, while diffuse allows shadows to modulate the surface.
 */
export function createBoardMaterial(
  color: 'White' | 'Gray',
  options: {
    transparent?: boolean;
    opacity?: number;
  } = {}
): THREE.MeshStandardMaterial {
  const { transparent = false, opacity = 1.0 } = options;
  const colorHex = getProductColor(color);

  const material = new THREE.MeshStandardMaterial({
    color: colorHex,             // Diffuse component — receives shadows
    roughness: 0.3,
    metalness: 0.1,
    emissive: colorHex,          // Emissive floor — keeps color bright in low light
    emissiveIntensity: 0.4,
    side: THREE.DoubleSide,
    transparent: transparent || opacity < 1.0,
    opacity,
    depthWrite: opacity > 0.9,
  });
  // Marks boards and trim for the designer's render settings (roughness,
  // texture depth…), which leave lumber, floor and openings alone.
  material.userData.kind = 'product';
  return material;
}

/** How see-through glass is with nothing reflected in it (its tint's opacity). */
export const GLASS_OPACITY = 0.16;

/**
 * Glass, for windows and for the glazed parts of doors and garage doors.
 *
 * Mostly clear, so you see through it — but a clear material in three shows
 * its reflections just as faintly as its tint, since both are scaled by the one
 * opacity. Real glass is the other way round: nearly invisible itself, with
 * whatever it reflects sitting on top at full strength. So the shader is
 * patched to lay two things over the clear tint, each making the glass more
 * opaque exactly where it is bright:
 *   - the ROOM, mirrored: a picture of the scene as the glass would reflect it
 *     (drawn by the render pipeline, one per glazed wall — the other walls, the
 *     floor, the light fixtures), stronger at a glancing angle, as glass is;
 *   - GLARE: the hot spots of the room's lamps.
 * Without a pipeline there is no mirror picture and the glass is simply clear.
 * Strengths are uniforms kept in `userData.glass`, set by the render settings.
 */
export function createGlassMaterial(): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color: 0xd3e6f0,
    roughness: 0.06,
    metalness: 0,
    emissive: 0xd3e6f0,
    emissiveIntensity: 0.1,
    vertexColors: true,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: GLASS_OPACITY,
    depthWrite: false,
  });
  const uniforms = {
    /** The room as this wall's glass mirrors it, and the matrix that finds a point's place in it. */
    tGlassMirror: { value: null as THREE.Texture | null },
    uGlassMirrorMatrix: { value: new THREE.Matrix4() },
    /** 0 while there is no mirror picture to show. */
    uGlassReflection: { value: 0 },
    uGlassGlare: { value: 1 },
    /** The glass's own opacity, to tell how far its wall has faded (opacity = this × the fade). */
    uGlassBase: { value: GLASS_OPACITY },
  };
  material.userData.kind = 'glass';
  material.userData.glass = uniforms;
  // The wall fade multiplies this rather than replacing it, so glass stays glass
  // (see transparencyController.applyOpacityToGroup).
  material.userData.baseOpacity = GLASS_OPACITY;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform mat4 uGlassMirrorMatrix;\nvarying vec4 vGlassMirror;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvGlassMirror = uGlassMirrorMatrix * modelMatrix * vec4( transformed, 1.0 );');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec4 vGlassMirror;
        uniform sampler2D tGlassMirror;
        uniform float uGlassReflection;
        uniform float uGlassGlare;
        uniform float uGlassBase;`)
      .replace('#include <opaque_fragment>', `
        {
          vec3 mirror = reflectedLight.directSpecular * uGlassGlare;
          if ( uGlassReflection > 0.0 && vGlassMirror.w > 0.0 ) {
            // Glass mirrors about 4% head-on and nearly everything at a glancing angle.
            float facing = saturate( dot( geometryNormal, geometryViewDir ) );
            float fresnel = 0.04 + 0.96 * pow( 1.0 - facing, 5.0 );
            vec3 room = max( texture2DProj( tGlassMirror, vGlassMirror ).rgb, vec3( 0.0 ) );
            mirror += room * min( 1.0, fresnel * uGlassReflection );
          }
          // As the wall fades out, so does everything on its glass.
          mirror *= clamp( diffuseColor.a / max( uGlassBase, 0.001 ), 0.0, 1.0 );
          vec3 tinted = ( totalDiffuse + totalEmissiveRadiance ) * diffuseColor.a;
          float cover = clamp( diffuseColor.a + max( mirror.r, max( mirror.g, mirror.b ) ), 0.0, 1.0 );
          outgoingLight = ( tinted + mirror ) / max( cover, 0.001 );
          diffuseColor.a = cover;
        }
        #include <opaque_fragment>`);
  };
  material.customProgramCacheKey = () => 'designer-glass-2';
  return material;
}

/** Default width of a seam between boards, in feet (1/8"). */
export const SEAM_WIDTH_FT = 0.125 / 12;

/**
 * Material for the seams drawn over board joints (see seamStrips.ts): a flat
 * dark tint, unlit, just in front of the boards.
 *
 * A seam is far thinner than a pixel at most zooms, so it is not left to the
 * renderer's anti-aliasing (which would catch one seam and miss the next). Each
 * seam is a generous quad carrying `aAcross` — the distance from the seam's
 * centre line, in feet — and the shader works out exactly how much of each
 * pixel the true-width seam covers. Every seam therefore reads the same, at any
 * zoom, and fades smoothly as it gets finer than a pixel.
 *
 * It starts fully transparent and hidden — the designer's render settings give
 * it its strength (`userData.baseOpacity`, which the wall fade multiplies into
 * `opacity`), and its render pipeline draws it in a pass of its own, on top of
 * the finished picture; viewers without that pipeline never show it.
 */
export function createSeamMaterial(): THREE.ShaderMaterial {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(0x20242a) },
      uOpacity: { value: 0 },
      uHalfWidth: { value: SEAM_WIDTH_FT / 2 },
    },
    vertexShader: /* glsl */`
      attribute float aAcross;
      varying float vAcross;
      void main() {
        vAcross = aAcross;
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }`,
    fragmentShader: /* glsl */`
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uHalfWidth;
      varying float vAcross;
      void main() {
        // One pixel's extent across the seam, in feet; then the share of that
        // extent the seam itself occupies.
        float pixel = max( fwidth( vAcross ), 1e-6 );
        float lo = max( vAcross - 0.5 * pixel, -uHalfWidth );
        float hi = min( vAcross + 0.5 * pixel, uHalfWidth );
        float coverage = clamp( ( hi - lo ) / pixel, 0.0, 1.0 );
        if ( coverage <= 0.0 ) discard;
        gl_FragColor = vec4( uColor, uOpacity * coverage );
      }`,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    side: THREE.DoubleSide,
    // Coplanar with the board faces, near enough: pull it forward in depth so
    // it never z-fights them.
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  material.extensions.derivatives = true;
  material.userData.kind = 'seam';
  material.userData.baseOpacity = 0;
  return material;
}

/**
 * Create a material for procedural 2x4 framing lumber.
 * SPF-style light tan tone with mild grain hint via roughness.
 * A fresh instance is returned each call so the room-rebuild disposal
 * loop in Room3DViewer can dispose it safely.
 *
 * `transparent: true` is set permanently. The renderer only animates
 * `opacity` (never toggles `transparent`) — toggling `transparent` at
 * runtime requires `material.needsUpdate = true` to recompile the shader,
 * and getting that wrong leaves studs stuck rendering at the wrong alpha.
 * Keeping it always-transparent with opacity=1 still renders fully opaque
 * pixels, just in the transparent pass — visually identical, no glitches.
 */
export function createLumberMaterial(): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color: 0xdcc096,      // SPF lumber tan (a touch lighter: the grain texture darkens it)
    roughness: 0.85,      // Wood is matte
    metalness: 0,
    emissive: 0x2a1f10,   // Subtle warm glow keeps studs visible in shadow
    emissiveIntensity: 0.15,
    side: THREE.FrontSide,
    transparent: true,
    opacity: 1,
    depthWrite: true,
  });
  // Procedural pine grain, knots and all (shared texture; see woodTexture.ts).
  applyWood(material, 'pine', 0.3);
  // For the designer's render settings (colour, relief).
  material.userData.kind = 'lumber';
  return material;
}

/**
 * Create a material for wall shell planes (backing behind boards).
 * Dark color so board gaps appear as visible seam lines.
 */
export function createWallShellMaterial(
  options: {
    transparent?: boolean;
    opacity?: number;
  } = {}
): THREE.MeshStandardMaterial {
  const { transparent = false, opacity = 1.0 } = options;

  return new THREE.MeshStandardMaterial({
    color: 0x444444,
    roughness: 0.9,
    metalness: 0.0,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: Math.min(opacity, 0.6),
    depthWrite: false,
  });
}

/**
 * Create a legacy wall material (solid color plane, used when showBoards=false).
 * Improved from original to render white accurately.
 */
export function createLegacyWallMaterial(
  color: 'White' | 'Gray',
  highlighted: boolean,
  options: {
    transparent?: boolean;
    opacity?: number;
  } = {}
): THREE.MeshStandardMaterial {
  const { transparent = true, opacity = 0.4 } = options;

  const materialColor = highlighted
    ? getProductColor(color)
    : 0xe5e7eb;

  return new THREE.MeshStandardMaterial({
    color: materialColor,
    side: THREE.DoubleSide,
    transparent,
    opacity,
    emissive: (highlighted && color === 'White') ? 0x333333 : 0x000000,
    emissiveIntensity: (highlighted && color === 'White') ? 0.2 : 0,
    roughness: 0.5,
    metalness: 0.0,
  });
}

/**
 * Create ceiling material (legacy mode).
 */
export function createLegacyCeilingMaterial(
  color: 'White' | 'Gray',
  highlighted: boolean,
  options: {
    transparent?: boolean;
    opacity?: number;
  } = {}
): THREE.MeshStandardMaterial {
  const { transparent = true, opacity = 0.3 } = options;

  const materialColor = highlighted
    ? getProductColor(color)
    : 0xf3f4f6;

  return new THREE.MeshStandardMaterial({
    color: materialColor,
    side: THREE.DoubleSide,
    transparent,
    opacity,
    emissive: (highlighted && color === 'White') ? 0x333333 : 0x000000,
    emissiveIntensity: (highlighted && color === 'White') ? 0.2 : 0,
    roughness: 0.5,
    metalness: 0.0,
  });
}

/**
 * Create floor material.
 */
/**
 * Generate a procedural concrete/orange-peel texture via canvas.
 * Creates a subtle mottled pattern with fine speckle noise.
 */
function createConcreteTexture(): THREE.CanvasTexture {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Base warm concrete color
  ctx.fillStyle = '#d4ccc0';
  ctx.fillRect(0, 0, size, size);

  // Larger mottled patches (orange peel effect)
  for (let i = 0; i < 800; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 2 + Math.random() * 6;
    const shade = 180 + Math.floor(Math.random() * 40);
    const alpha = 0.08 + Math.random() * 0.12;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${shade}, ${shade - 8}, ${shade - 16}, ${alpha})`;
    ctx.fill();
  }

  // Fine speckle noise
  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 14;
    data[i] = Math.min(255, Math.max(0, data[i] + noise));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise));
  }
  ctx.putImageData(imageData, 0, 0);

  // Subtle darker streaks
  for (let i = 0; i < 15; i++) {
    const x1 = Math.random() * size;
    const y1 = Math.random() * size;
    const x2 = x1 + (Math.random() - 0.5) * 200;
    const y2 = y1 + (Math.random() - 0.5) * 200;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = `rgba(140, 130, 120, ${0.04 + Math.random() * 0.06})`;
    ctx.lineWidth = 1 + Math.random() * 3;
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  return texture;
}

/**
 * Create floor material. Loads dirty concrete bitmap by default.
 * The texture loads asynchronously; material starts with the procedural
 * fallback and swaps to the bitmap once loaded.
 */
export function createFloorMaterial(
  highlighted: boolean,
  onTextureLoaded?: () => void
): THREE.MeshStandardMaterial {
  // Start with procedural texture as immediate fallback
  const procTexture = createConcreteTexture();

  const mat = new THREE.MeshStandardMaterial({
    map: procTexture,
    color: highlighted ? 0xd4c5a0 : 0xffffff,
    side: THREE.DoubleSide,
    transparent: false,
    opacity: 1.0,
    roughness: 1.0,
    metalness: 0.0,
  });

  // Load the default bitmap texture asynchronously
  const loader = new THREE.TextureLoader();
  loader.load('/textures/floor-concrete-dirty.jpg', (texture) => {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(3, 3);
    texture.colorSpace = THREE.SRGBColorSpace;
    procTexture.dispose();
    mat.map = texture;
    mat.needsUpdate = true;
    if (onTextureLoaded) onTextureLoaded();
  });

  return mat;
}

// ============================================================================
// THE YARD (yardBuilder.ts): the slabs' concrete and the lawn's grass
// ============================================================================

let slabTexture: THREE.CanvasTexture | null = null;
let grassTexture: THREE.CanvasTexture | null = null;

/** A canvas drawn once and tiled; a flat colour where there is no 2D context (jsdom). */
function tiledCanvasTexture(size: number, fallback: string, draw: (ctx: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = fallback;
    ctx.fillRect(0, 0, size, size);
    draw(ctx);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
}

/**
 * Broom-finished concrete, one 10 ft bay per tile: a light mottled grey,
 * faint parallel broom strokes, and the expansion joint along the tile's
 * edges. Built once per page (the yard's meshes share it through their UVs).
 */
export function createSlabTexture(): THREE.CanvasTexture {
  if (slabTexture) return slabTexture;
  const size = 512;
  slabTexture = tiledCanvasTexture(size, '#d6d3cc', (ctx) => {
    for (let i = 0; i < 700; i++) {
      const shade = 190 + Math.floor(Math.random() * 40);
      ctx.beginPath();
      ctx.arc(Math.random() * size, Math.random() * size, 2 + Math.random() * 7, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${shade}, ${shade - 3}, ${shade - 8}, ${0.08 + Math.random() * 0.1})`;
      ctx.fill();
    }
    const image = ctx.getImageData(0, 0, size, size);
    const d = image.data;
    for (let i = 0; i < d.length; i += 4) {
      const n = (Math.random() - 0.5) * 12;
      d[i] = Math.min(255, Math.max(0, d[i] + n));
      d[i + 1] = Math.min(255, Math.max(0, d[i + 1] + n));
      d[i + 2] = Math.min(255, Math.max(0, d[i + 2] + n));
    }
    ctx.putImageData(image, 0, 0);
    // Broom strokes: fine lines across the tile, alternately a shade darker and lighter.
    for (let y = 0; y < size; y += 3) {
      ctx.fillStyle = (y / 3) % 2 === 0 ? 'rgba(0, 0, 0, 0.035)' : 'rgba(255, 255, 255, 0.03)';
      ctx.fillRect(0, y + Math.floor(Math.random() * 2), size, 1);
    }
    // The joint, cut along the tile's left and top edges (the tile is one bay).
    ctx.fillStyle = 'rgba(40, 40, 40, 0.35)';
    ctx.fillRect(0, 0, size, 2);
    ctx.fillRect(0, 0, 2, size);
  });
  return slabTexture;
}

/**
 * A lawn, 4 ft per tile: a mid green with thousands of short blade strokes
 * in a range of greens, a few soft patches where it is thinner or richer,
 * and grain. Built once per page.
 */
export function createGrassTexture(): THREE.CanvasTexture {
  if (grassTexture) return grassTexture;
  const size = 512;
  grassTexture = tiledCanvasTexture(size, '#4e7a36', (ctx) => {
    const patch = (fill: string) => {
      const x = Math.random() * size, y = Math.random() * size, r = 60 + Math.random() * 60;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, fill);
      g.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = g;
      // Drawn four times so a patch on an edge wraps round to the other side.
      for (const dx of [0, size, -size]) for (const dz of [0, size, -size]) { ctx.save(); ctx.translate(dx, dz); ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); ctx.restore(); }
    };
    for (let i = 0; i < 3; i++) patch('rgba(150, 160, 60, 0.12)');
    for (let i = 0; i < 3; i++) patch('rgba(20, 50, 15, 0.14)');
    const greens = ['#3c6a2a', '#45762f', '#4f8236', '#5b8f3d', '#6a9d45', '#7aa548', '#38622a'];
    for (let i = 0; i < 7000; i++) {
      const x = Math.random() * size, y = Math.random() * size;
      const len = 2 + Math.random() * 4;
      const a = -Math.PI / 2 + (Math.random() - 0.5) * 0.9;
      ctx.strokeStyle = greens[Math.floor(Math.random() * greens.length)];
      ctx.globalAlpha = 0.35 + Math.random() * 0.3;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const image = ctx.getImageData(0, 0, size, size);
    const d = image.data;
    for (let i = 0; i < d.length; i += 4) {
      const n = (Math.random() - 0.5) * 16;
      d[i] = Math.min(255, Math.max(0, d[i] + n * 0.6));
      d[i + 1] = Math.min(255, Math.max(0, d[i + 1] + n));
      d[i + 2] = Math.min(255, Math.max(0, d[i + 2] + n * 0.4));
    }
    ctx.putImageData(image, 0, 0);
  });
  return grassTexture;
}

/** The driveway's and landings' concrete. `userData.kind` is what the export tags it (sceneExport.ts). */
export function createSlabMaterial(): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ map: createSlabTexture(), color: 0xffffff, roughness: 0.95, metalness: 0 });
  m.userData.kind = 'slab';
  m.userData.exportColor = '#cfccc5';
  return m;
}

/** The fence's boards: a plain wood tone (the renderer draws the grain and the pickets' grooves from the fence's UVs). */
export function createFenceMaterial(): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color: 0x9b7b55, roughness: 0.85, metalness: 0 });
  m.userData.kind = 'fence';
  m.userData.exportColor = '#9b7b55';
  return m;
}

/** The lawn. Its own picture stands in for a bump map, for a little relief in the view. */
export function createLawnMaterial(): THREE.MeshStandardMaterial {
  const tex = createGrassTexture();
  const m = new THREE.MeshStandardMaterial({ map: tex, bumpMap: tex, bumpScale: 0.02, color: 0xffffff, roughness: 1, metalness: 0 });
  m.userData.kind = 'lawn';
  m.userData.exportColor = '#4e7a36';
  return m;
}
